import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { mediaUrl } from "@/lib/api";
import { useI18n, LANGS } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin, Wifi, Sofa, GraduationCap, ArrowRight, Lock, Loader2, CheckCircle2, Sparkles, Phone, Mail } from "lucide-react";
import { ICONS } from "@/lib/amenityIcons";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1618323053726-71addccf3f81?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjB0cm9waWNhbCUyMHJlc2lkZW5jZSUyMGJ1aWxkaW5nJTIwVGhhaWxhbmR8ZW58MHx8fHwxNzg2MTg0OTIxfDA&ixlib=rb-4.1.0&q=85";
const GALLERY = [
  "https://images.unsplash.com/photo-1585128792103-0b591f96512e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdHVkaW8lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3ODYxODQ5MjF8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1702014862053-946a122b920d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzdHVkaW8lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGJyaWdodHxlbnwwfHx8fDE3ODYxODQ5MjF8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1585370657610-c0a269c56f38?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB0cm9waWNhbCUyMHJlc2lkZW5jZSUyMGJ1aWxkaW5nJTIwVGhhaWxhbmR8ZW58MHx8fHwxNzg2MTg0OTIxfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1687239292446-d2baf1a36c09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwyfHxydXJhbCUyMFRoYWlsYW5kJTIwY291bnRyeXNpZGUlMjBncmVlbiUyMHJpY2UlMjBmaWVsZHxlbnwwfHx8fDE3ODYxODUyMTd8MA&ixlib=rb-4.1.0&q=85",
];
const LOCATION = "Selaphum, Roi Et — Thaïlande";

const emptyForm = { name: "", email: "", phone: "", room: "", move_in: "", message: "" };

export default function PublicSite() {
  const { t, lang, changeLang } = useI18n();
  const navigate = useNavigate();
  const { data: rooms = [] } = useQuery({ queryKey: ["public-rooms"], queryFn: () => api.get("/public/rooms").then((r) => r.data) });
  const { data: content } = useQuery({ queryKey: ["public-content"], queryFn: () => api.get("/public/content").then((r) => r.data) });
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const minRent = rooms.length ? Math.min(...rooms.map((r) => r.base_rent || 0).filter(Boolean)) : 0;

  const L = (o) => { if (o == null) return ""; if (typeof o === "string") return o; return o[lang] || o.fr || o.en || o.th || ""; };
  const heroTitle = L(content?.hero?.title) || t("site.name");
  const tagline = L(content?.hero?.tagline) || t("site.tagline");
  const intro = L(content?.hero?.intro) || t("site.intro");
  const amenities = content?.amenities || [];
  const feats = amenities.length
    ? amenities.map((a) => ({ Ico: ICONS[a.icon] || Sparkles, title: L(a.title), desc: L(a.desc) }))
    : [{ Ico: Sofa, title: t("site.features1") }, { Ico: Wifi, title: t("site.features2") }, { Ico: GraduationCap, title: t("site.features3") }];
  const locTitle = L(content?.location?.title) || t("site.locationTitle");
  const locText = L(content?.location?.text) || t("site.locationText");
  const contact = content?.contact || {};
  const contactNote = L(contact.note) || t("site.contactSub");
  const contactAddr = L(contact.address);
  const heroImg = mediaUrl(content?.hero_image) || HERO;
  const galleryImgs = (content?.gallery || []).length ? content.gallery.map(mediaUrl) : GALLERY;
  const roomImage = (i, r) => mediaUrl(content?.room_images?.[r.number]) || GALLERY[i % 2];
  const floorLabel = (f) => (f === "ground" || f === "first") ? t(`unit.floor_${f}`) : (f || "");
  const mLat = parseFloat(content?.location?.lat);
  const mLng = parseFloat(content?.location?.lng);
  const hasMap = !isNaN(mLat) && !isNaN(mLng);
  const mapSrc = hasMap ? `https://www.openstreetmap.org/export/embed.html?bbox=${mLng - 0.01}%2C${mLat - 0.01}%2C${mLng + 0.01}%2C${mLat + 0.01}&layer=mapnik&marker=${mLat}%2C${mLng}` : "";
  const mapLink = hasMap ? `https://www.openstreetmap.org/?mlat=${mLat}&mlon=${mLng}#map=16/${mLat}/${mLng}` : "";
  const attractions = content?.attractions || [];
  const attSrc = (img) => (!img ? "" : img.startsWith("http") ? img : mediaUrl(img));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error(t("site.formName")); return; }
    setSending(true);
    try {
      await api.post("/public/inquiries", { ...form, lang });
      setSent(true); setForm(emptyForm);
      toast.success(t("site.sent"));
    } catch { toast.error("Error"); }
    setSending(false);
  };

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-linen font-sans text-[#2A2A2A]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-linen/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-teak flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
            <div className="font-semibold text-teak leading-tight">{t("site.name")}<span className="hidden sm:inline text-xs text-muted-foreground font-normal"> · {LOCATION}</span></div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <button onClick={() => scrollTo("rooms")} className="hover:text-teak transition-colors" data-testid="site-nav-rooms">{t("site.nav_rooms")}</button>
            <button onClick={() => scrollTo("location")} className="hover:text-teak transition-colors" data-testid="site-nav-location">{t("site.nav_location")}</button>
            <button onClick={() => scrollTo("contact")} className="hover:text-teak transition-colors" data-testid="site-nav-contact">{t("site.nav_contact")}</button>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="flex items-center gap-1 bg-white rounded-full border border-border p-1">
              {LANGS.map((l) => (
                <button key={l.code} onClick={() => changeLang(l.code)} data-testid={`site-lang-${l.code}`}
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors ${lang === l.code ? "bg-teak text-white" : "text-muted-foreground"}`}>{l.label}</button>
              ))}
            </div>
            <button onClick={() => navigate("/login")} data-testid="header-admin-link"
              className="flex items-center gap-1.5 rounded-full bg-teak text-white px-3 py-1.5 text-xs font-medium hover:bg-teak-hover transition-colors whitespace-nowrap">
              <Lock className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t("site.adminLink")}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-teak/55" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-28 md:py-40 text-white">
          <p className="text-xs uppercase tracking-[0.25em] text-gold mb-4">{LOCATION}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight max-w-2xl">{heroTitle}</h1>
          <p className="text-lg md:text-xl text-white/85 mt-4 max-w-xl font-light">{tagline}</p>
          <p className="text-white/75 mt-4 max-w-xl leading-relaxed">{intro}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={() => scrollTo("rooms")} className="bg-gold hover:bg-gold/90 text-[#2A2A2A] rounded-full px-6 h-12" data-testid="hero-cta">
              {t("site.heroCta")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => scrollTo("contact")} variant="outline" className="rounded-full px-6 h-12 bg-transparent border-white/60 text-white hover:bg-white/10 hover:text-white" data-testid="hero-contact">
              {t("site.nav_contact")}
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-14 grid sm:grid-cols-3 gap-6">
        {feats.map((f, i) => (
          <div key={i} className="flex items-start gap-4 bg-white rounded-lg border border-border p-5" data-testid={`site-feature-${i}`}>
            <div className="w-11 h-11 rounded-md bg-terracotta-light flex items-center justify-center shrink-0"><f.Ico className="w-5 h-5 text-terracotta" /></div>
            <div>
              <span className="font-medium block">{f.title}</span>
              {f.desc && <span className="text-sm text-muted-foreground">{f.desc}</span>}
            </div>
          </div>
        ))}
      </section>

      {/* Rooms */}
      <section id="rooms" className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.1em] text-terracotta">{t("site.from")} {money(minRent)} {t("site.perMonth")}</p>
          <h2 className="text-3xl font-light tracking-tight text-teak mt-1">{t("site.roomsTitle")}</h2>
          <p className="text-muted-foreground">{t("site.roomsSub")}</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rooms.map((r, i) => (
            <div key={r.number} className="bg-white rounded-lg border border-border overflow-hidden group" data-testid={`site-room-${r.number}`}>
              <div className="relative h-44 overflow-hidden">
                <img src={roomImage(i, r)} alt={r.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                <span className={`absolute top-3 right-3 text-[11px] font-medium px-2.5 py-1 rounded-full ${r.available ? "bg-[#EAF4F0] text-[#2D6A4F]" : "bg-[#F9EAEB] text-destructive"}`}>
                  {r.available ? t("site.available") : t("site.occupied")}
                </span>
              </div>
              <div className="p-5">
                <div className="flex items-baseline justify-between">
                  <span className="text-xl font-semibold text-teak">{r.number}</span>
                  <div className="text-right">
                    <span className="text-lg font-semibold">{money(r.base_rent)}<span className="text-sm text-muted-foreground font-normal">{t("site.perMonth")}</span></span>
                    {(r.price_weekly > 0 || r.price_daily > 0) && (
                      <div className="text-xs text-muted-foreground" data-testid={`site-room-shortprice-${r.number}`}>
                        {r.price_weekly > 0 && <span>{money(r.price_weekly)}{t("site.perWeek")}</span>}
                        {r.price_weekly > 0 && r.price_daily > 0 && <span> · </span>}
                        {r.price_daily > 0 && <span>{money(r.price_daily)}{t("site.perDay")}</span>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground capitalize mt-1">{t(`unit.type_${r.type}`)} · {floorLabel(r.floor)}</div>
                <Button onClick={() => { setForm((f) => ({ ...f, room: r.number })); scrollTo("contact"); }}
                  variant="outline" size="sm" className="w-full mt-4 rounded-full" disabled={!r.available} data-testid={`site-room-cta-${r.number}`}>
                  {t("site.nav_contact")}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <h2 className="text-3xl font-light tracking-tight text-teak mb-6">{t("site.galleryTitle")}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {galleryImgs.map((g, i) => (
            <div key={i} className="rounded-lg overflow-hidden aspect-[4/3]">
              <img src={g} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
            </div>
          ))}
        </div>
      </section>

      {/* Location */}
      {attractions.length > 0 && (
        <section id="discover" className="bg-white border-y border-border">
          <div className="max-w-6xl mx-auto px-5 md:px-8 py-14">
            <h2 className="text-3xl font-light tracking-tight text-teak">{t("site.discoverTitle")}</h2>
            <p className="text-muted-foreground mt-1">{t("site.discoverSub")}</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {attractions.map((a, i) => (
                <div key={i} className="rounded-lg overflow-hidden border border-border bg-sand/30 group" data-testid={`site-attraction-${i}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    {attSrc(a.image)
                      ? <img src={attSrc(a.image)} alt={L(a.title)} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      : <div className="w-full h-full flex items-center justify-center bg-sand"><MapPin className="w-8 h-8 text-teak/40" /></div>}
                  </div>
                  <div className="p-4">
                    <h3 className="font-medium text-teak">{L(a.title)}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{L(a.desc)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="location" className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="bg-white rounded-lg border border-border p-8 flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-14 h-14 rounded-md bg-teak/10 flex items-center justify-center shrink-0"><MapPin className="w-7 h-7 text-teak" /></div>
          <div>
            <h2 className="text-2xl font-light tracking-tight text-teak">{locTitle}</h2>
            <p className="text-muted-foreground mt-1">{locText}</p>
          </div>
        </div>
        {hasMap && (
          <div className="mt-4 rounded-lg overflow-hidden border border-border" data-testid="site-map">
            <iframe title="map" src={mapSrc} className="w-full h-72 border-0" loading="lazy"></iframe>
            <div className="bg-white p-3 text-center">
              <a href={mapLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-teak hover:underline" data-testid="site-map-link"><MapPin className="w-4 h-4" />{t("site.openMap")}</a>
            </div>
          </div>
        )}
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-3xl mx-auto px-5 md:px-8 py-14">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-light tracking-tight text-teak">{t("site.contactTitle")}</h2>
          <p className="text-muted-foreground mt-1">{contactNote}</p>
          {(contact.phone || contact.email || contact.line || contactAddr) && (
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-4 text-sm text-teak" data-testid="site-contact-info">
              {contact.phone && <a href={`tel:${contact.phone}`} className="flex items-center gap-1.5 hover:underline"><Phone className="w-4 h-4" />{contact.phone}</a>}
              {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 hover:underline"><Mail className="w-4 h-4" />{contact.email}</a>}
              {contact.line && <span className="flex items-center gap-1.5">LINE: {contact.line}</span>}
              {contactAddr && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{contactAddr}</span>}
            </div>
          )}
        </div>
        {sent ? (
          <div className="bg-white rounded-lg border border-border p-10 text-center" data-testid="inquiry-success">
            <CheckCircle2 className="w-14 h-14 text-[#2D6A4F] mx-auto mb-4" />
            <p className="text-lg">{t("site.sent")}</p>
            <Button variant="outline" className="mt-6 rounded-full" onClick={() => setSent(false)}>{t("site.nav_contact")}</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white rounded-lg border border-border p-6 md:p-8 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("site.formName")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="inquiry-name" /></div>
              <div className="space-y-2"><Label>{t("site.formPhone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquiry-phone" /></div>
              <div className="space-y-2"><Label>{t("site.formEmail")}</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquiry-email" /></div>
              <div className="space-y-2"><Label>{t("site.formMoveIn")}</Label><Input type="date" value={form.move_in} onChange={(e) => setForm({ ...form, move_in: e.target.value })} data-testid="inquiry-movein" /></div>
              <div className="space-y-2 sm:col-span-2"><Label>{t("site.formRoom")}</Label>
                <Select value={form.room} onValueChange={(v) => setForm({ ...form, room: v })}>
                  <SelectTrigger data-testid="inquiry-room"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{rooms.filter((r) => r.available).map((r) => <SelectItem key={r.number} value={r.number}>{r.number} · {money(r.base_rent)}{t("site.perMonth")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>{t("site.formMessage")}</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inquiry-message" /></div>
            <Button type="submit" disabled={sending} className="w-full h-11 bg-teak hover:bg-teak-hover text-white rounded-full" data-testid="inquiry-submit">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : t("site.send")}
            </Button>
          </form>
        )}
      </section>

      {/* Footer with discreet admin link */}
      <footer className="border-t border-border bg-sand/40">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© {new Date().getFullYear()} {t("site.name")} · ng-residence.com</div>
          <button onClick={() => navigate("/login")} data-testid="admin-access-link"
            className="flex items-center gap-1.5 opacity-60 hover:opacity-100 hover:text-teak transition-all">
            <Lock className="w-3.5 h-3.5" /> {t("site.adminLink")}
          </button>
        </div>
      </footer>
    </div>
  );
}
